/**
 * PROFLEX theme JS
 * Handles: sticky/blur header, mobile drawer, lead modal + validation + submit,
 * hero video modal, PROFLEX product gallery + lightbox + zoom, exercise library filter,
 * marquee auto-scroll, scroll-fade reveal animations, analytics events.
 *
 * Every overlay (drawer, lead modal, video modal, lightbox) toggles pointer-events
 * directly in JS as a fail-safe alongside the CSS is-open rules, so a closed
 * overlay can never trap clicks or change the cursor over content beneath it
 * (including the fixed header), even if a class fails to apply cleanly.
 *
 * Each init function is now called through safeInit(), which wraps it in a
 * try/catch. Previously, if any single init function threw an error, every
 * init function listed after it in the DOMContentLoaded block would never
 * run — silently disabling clicks on the header, Buy Now, gallery, etc. even
 * though those features had nothing to do with the actual error. safeInit()
 * guarantees one broken feature can never disable any other feature again.
 */

(function () {
  'use strict';

  /* -----------------------------
     Utilities
  ----------------------------- */
  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function trapFocus(container) {
    var focusable = qsa(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      container
    ).filter(function (el) { return !el.hasAttribute('disabled'); });
    if (!focusable.length) return function () {};
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    function handleKey(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handleKey);
    first.focus();
    return function () {
      container.removeEventListener('keydown', handleKey);
    };
  }

  function dispatchAnalyticsEvent(eventName, detail) {
    document.dispatchEvent(new CustomEvent('proflex:analytics', {
      detail: Object.assign({ event: eventName }, detail || {})
    }));
  }

  function safeInit(name, fn) {
    try {
      fn();
    } catch (err) {
      // Log clearly so it's easy to spot in DevTools Console, but never
      // let this stop any other init function from running.
      if (window.console && console.error) {
        console.error('[PROFLEX] "' + name + '" failed to initialize:', err);
      }
    }
  }

  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* -----------------------------
     Header: sticky + blur on scroll
  ----------------------------- */
  function initHeader() {
    var header = qs('[data-proflex-header]');
    if (!header) return;

    var THRESHOLD = 24;
    var ticking = false;

    function update() {
      var scrolled = window.scrollY > THRESHOLD;
      header.classList.toggle('is-scrolled', scrolled);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  /* -----------------------------
     Mobile drawer nav
  ----------------------------- */
  function initMobileDrawer() {
    var toggle = qs('[data-drawer-toggle]');
    var drawer = qs('[data-mobile-drawer]');
    if (!toggle || !drawer) return;

    var releaseFocus = null;

    function openDrawer() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.style.pointerEvents = 'auto';
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('proflex-scroll-lock');
      releaseFocus = trapFocus(drawer);
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.style.pointerEvents = 'none';
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('proflex-scroll-lock');
      if (releaseFocus) releaseFocus();
      toggle.focus();
    }

    toggle.addEventListener('click', function () {
      var isOpen = drawer.classList.contains('is-open');
      if (isOpen) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });

    qsa('[data-drawer-close]', drawer).forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });
  }

  /* -----------------------------
     Lead modal (Buy Now)
  ----------------------------- */
  function initLeadModal() {
    var modal = qs('#proflex-lead-modal');
    if (!modal) return;

    var openers = qsa('[data-open-lead-modal]');
    var closers = qsa('[data-modal-close]', modal);
    var lastFocused = null;
    var releaseFocus = null;

    function openModal(sourceLabel) {
      lastFocused = document.activeElement;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      modal.style.pointerEvents = 'auto';
      document.body.classList.add('proflex-scroll-lock');

      var sourceInput = qs('[data-lead-form-source]', modal);
      if (sourceInput) sourceInput.value = sourceLabel || '';

      releaseFocus = trapFocus(qs('.proflex-modal__panel', modal));
      dispatchAnalyticsEvent('buy_now_click', { source: sourceLabel || '' });
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      modal.style.pointerEvents = 'none';
      document.body.classList.remove('proflex-scroll-lock');
      if (releaseFocus) releaseFocus();
      if (lastFocused) lastFocused.focus();
      resetForm();
    }

    function resetForm() {
      var form = qs('[data-lead-form-element]', modal);
      var success = qs('[data-lead-form-success]', modal);
      var error = qs('[data-lead-form-error]', modal);
      var input = qs('[data-lead-form-input]', modal);
      if (form) {
        form.hidden = false;
        form.reset();
      }
      if (success) success.hidden = true;
      if (error) error.hidden = true;
      if (input) input.setAttribute('aria-invalid', 'false');
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openModal(btn.getAttribute('data-open-lead-modal'));
      });
    });

    closers.forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) {
        closeModal();
      }
    });
  }

  /* -----------------------------
     Mobile number validation + submit
     Supports two modes via data-webhook-endpoint on the form:
     - Blank: submit as FormData to Shopify's native customer form handler
     - Set: submit as JSON to an external webhook (Zapier, Make, CRM, etc.)
  ----------------------------- */
  function isValidMobile(value) {
    return /^[0-9]{10}$/.test(value);
  }

  function initLeadForm() {
    var form = qs('[data-lead-form-element]');
    if (!form) return;

    var input = qs('[data-lead-form-input]', form);
    var errorEl = qs('[data-lead-form-error]', form);
    var submitBtn = qs('[data-lead-form-submit]', form);
    var submitText = qs('[data-lead-form-submit-text]', form);
    var spinner = qs('[data-lead-form-spinner]', form);
    var successEl = qs('[data-lead-form-success]', form.closest('.proflex-lead-form'));
    var webhookEndpoint = form.getAttribute('data-webhook-endpoint');
    var isWebhookMode = !!(webhookEndpoint && webhookEndpoint.trim().length > 0);

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || 'Please enter full mobile number';
      errorEl.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.classList.add('is-invalid');
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      input.setAttribute('aria-invalid', 'false');
      input.classList.remove('is-invalid');
    }

    input.addEventListener('input', function () {
      var digitsOnly = input.value.replace(/[^0-9]/g, '').slice(0, 10);
      input.value = digitsOnly;
      if (digitsOnly.length === 0 || isValidMobile(digitsOnly) || digitsOnly.length < 10) {
        clearError();
      }
    });

    input.addEventListener('blur', function () {
      if (input.value.length > 0 && !isValidMobile(input.value)) {
        showError('Please enter full mobile number');
      }
    });

    function setLoading(isLoading) {
      submitBtn.disabled = isLoading;
      if (spinner) spinner.hidden = !isLoading;
      if (submitText) submitText.style.visibility = isLoading ? 'hidden' : 'visible';
    }

    function onSuccess(value) {
      setLoading(false);
      form.hidden = true;
      if (successEl) successEl.hidden = false;
      dispatchAnalyticsEvent('lead_form_submit', { mobile_number: value });
    }

    function onFailure() {
      setLoading(false);
      showError('Something went wrong. Please try again.');
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var value = input.value.trim();
      if (!isValidMobile(value)) {
        showError('Please enter full mobile number');
        input.focus();
        return;
      }
      clearError();
      setLoading(true);

      var sourceInput = qs('[data-lead-form-source]', form);
      var source = sourceInput ? sourceInput.value : '';

      if (isWebhookMode) {
        fetch(webhookEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mobile_number: value,
            country_code: '+91',
            source: source,
            page_url: window.location.href,
            timestamp: new Date().toISOString()
          })
        })
          .then(function () { onSuccess(value); })
          .catch(onFailure);
      } else {
        var formData = new FormData(form);
        fetch(form.getAttribute('action'), {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' }
        })
          .then(function () { onSuccess(value); })
          .catch(onFailure);
      }
    });
  }

  /* -----------------------------
     Video play tracking (generic, e.g. exercise cards)
  ----------------------------- */
  function initVideoTracking() {
    qsa('[data-video-play-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        dispatchAnalyticsEvent('video_play', {
          video_id: trigger.getAttribute('data-video-play-trigger') || ''
        });
      });
    });
  }

  /* -----------------------------
     Video modal (Hero "Watch Video")
  ----------------------------- */
  function initVideoModal() {
    var modal = qs('[data-video-modal]');
    if (!modal) return;

    var openers = qsa('[data-open-video-modal]');
    var closers = qsa('[data-video-modal-close]', modal);
    var player = qs('[data-video-modal-player]', modal);
    var lastFocused = null;
    var releaseFocus = null;

    function openModal() {
      lastFocused = document.activeElement;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      modal.style.pointerEvents = 'auto';
      document.body.classList.add('proflex-scroll-lock');
      releaseFocus = trapFocus(qs('.proflex-video-modal__panel', modal));
      if (player) {
        player.currentTime = 0;
        player.play().catch(function () {});
      }
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      modal.style.pointerEvents = 'none';
      document.body.classList.remove('proflex-scroll-lock');
      if (player) player.pause();
      if (releaseFocus) releaseFocus();
      if (lastFocused) lastFocused.focus();
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openModal();
      });
    });

    closers.forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) {
        closeModal();
      }
    });
  }

  /* -----------------------------
     PROFLEX Product Gallery (thumbs, viewport, lightbox, zoom)
     No hover-zoom/magnifier is ever wired up here — the only
     zoom entry point is clicking the main image, which opens
     the full-screen lightbox.

     Iterates every [data-proflex-gallery] found on the page
     (not just the first) so behavior is correct even if a
     duplicate/leftover gallery section is present.
  ----------------------------- */
  function initProflexProductGallery() {
    var sections = qsa('[data-proflex-gallery]');
    if (!sections.length) return;

    sections.forEach(function (section) {
      safeInit('gallery instance', function () {
        initSingleGallery(section);
      });
    });

    safeInit('gallery lightbox', initLightbox);
  }

  function initSingleGallery(section) {
    var thumbBtns = qsa('[data-gallery-thumb-btn]', section);
    var slides = qsa('[data-gallery-slide]', section);
    var viewport = qs('[data-gallery-viewport]', section);
    var prevBtn = qs('[data-gallery-prev]', section);
    var nextBtn = qs('[data-gallery-next]', section);
    var counterCurrent = qs('[data-gallery-counter-current]', section);
    var total = slides.length;
    if (!total) return;

    var currentIndex = 0;

    function pauseVideos(root) {
      qsa('video', root).forEach(function (v) { v.pause(); });
    }

    function goTo(index, opts) {
      opts = opts || {};
      var newIndex = (index + total) % total;
      var prevIndex = currentIndex;
      currentIndex = newIndex;

      slides.forEach(function (slide, i) {
        var active = i === newIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      thumbBtns.forEach(function (btn, i) {
        var active = i === newIndex;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });

      if (counterCurrent) counterCurrent.textContent = newIndex + 1;

      pauseVideos(slides[prevIndex]);

      if (!opts.silent) {
        dispatchAnalyticsEvent('gallery_image_viewed', { index: newIndex });
      }
    }

    thumbBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var index = parseInt(btn.getAttribute('data-index'), 10) || 0;
        goTo(index);
        dispatchAnalyticsEvent('thumbnail_clicked', { index: index });
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        goTo(currentIndex - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        goTo(currentIndex + 1);
      });
    }

    if (viewport) {
      viewport.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(currentIndex - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(currentIndex + 1); }
      });

      var touchStartX = 0;
      var touchEndX = 0;

      viewport.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      viewport.addEventListener('touchend', function (e) {
        touchEndX = e.changedTouches[0].screenX;
        var delta = touchEndX - touchStartX;
        if (Math.abs(delta) > 40) {
          if (delta < 0) {
            goTo(currentIndex + 1);
          } else {
            goTo(currentIndex - 1);
          }
        }
      }, { passive: true });
    }

    qsa('[data-gallery-video]', section).forEach(function (wrap) {
      var video = qs('video', wrap);
      if (!video) return;
      video.addEventListener('play', function () {
        dispatchAnalyticsEvent('video_play', { index: wrap.getAttribute('data-index') });
      });
    });

    var openTriggers = qsa('[data-lightbox-open]', section);
    openTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var index = parseInt(trigger.getAttribute('data-index'), 10) || 0;
        document.dispatchEvent(new CustomEvent('proflex:open-lightbox', { detail: { index: index } }));
      });
    });

    // Ensure initial DOM state matches JS state (defensive: in case
    // Liquid's initial is-active markup and JS ever drift apart).
    goTo(0, { silent: true });
  }

  /* -----------------------------
     Lightbox — opens ONLY on main image click (via the
     'proflex:open-lightbox' event dispatched from the gallery).
  ----------------------------- */
  function initLightbox() {
    var lightbox = qs('[data-lightbox]');
    if (!lightbox) return;
    if (lightbox.__proflexLightboxInitialized) return;
    lightbox.__proflexLightboxInitialized = true;

    var lightboxItems = qsa('[data-lightbox-item]', lightbox);
    var lightboxCloseBtn = qs('[data-lightbox-close]', lightbox);
    var lightboxPrevBtn = qs('[data-lightbox-prev]', lightbox);
    var lightboxNextBtn = qs('[data-lightbox-next]', lightbox);
    var lightboxCounterCurrent = qs('[data-lightbox-counter-current]', lightbox);
    var zoomToggleBtn = qs('[data-lightbox-zoom-toggle]', lightbox);

    var lightboxIndex = 0;
    var lastFocused = null;
    var releaseFocus = null;
    var isZoomed = false;

    function pauseVideos(root) {
      qsa('video', root).forEach(function (v) { v.pause(); });
    }

    function showLightboxItem(index) {
      lightboxIndex = (index + lightboxItems.length) % lightboxItems.length;
      lightboxItems.forEach(function (item, i) {
        var active = i === lightboxIndex;
        item.classList.toggle('is-active', active);
        if (!active) pauseVideos(item);
      });
      if (lightboxCounterCurrent) lightboxCounterCurrent.textContent = lightboxIndex + 1;
      resetZoom();
    }

    function resetZoom() {
      isZoomed = false;
      qsa('[data-lightbox-zoom-wrap]', lightbox).forEach(function (wrap) {
        wrap.classList.remove('is-zoomed');
        wrap.style.transformOrigin = '50% 50%';
      });
      if (zoomToggleBtn) zoomToggleBtn.setAttribute('aria-pressed', 'false');
    }

    function openLightbox(index) {
      lastFocused = document.activeElement;
      showLightboxItem(index);
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      lightbox.style.pointerEvents = 'auto';
      document.body.classList.add('proflex-scroll-lock');
      releaseFocus = trapFocus(lightbox);
      dispatchAnalyticsEvent('lightbox_opened', { index: lightboxIndex });
    }

    function closeLightbox() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      lightbox.style.pointerEvents = 'none';
      document.body.classList.remove('proflex-scroll-lock');
      pauseVideos(lightbox);
      resetZoom();
      if (releaseFocus) releaseFocus();
      if (lastFocused) lastFocused.focus();
      dispatchAnalyticsEvent('lightbox_closed', { index: lightboxIndex });
    }

    document.addEventListener('proflex:open-lightbox', function (e) {
      openLightbox(e.detail && typeof e.detail.index === 'number' ? e.detail.index : 0);
    });

    if (lightboxCloseBtn) lightboxCloseBtn.addEventListener('click', closeLightbox);
    if (lightboxPrevBtn) lightboxPrevBtn.addEventListener('click', function () { showLightboxItem(lightboxIndex - 1); });
    if (lightboxNextBtn) lightboxNextBtn.addEventListener('click', function () { showLightboxItem(lightboxIndex + 1); });

    if (zoomToggleBtn) {
      zoomToggleBtn.addEventListener('click', function () {
        var activeWrap = qs('.proflex-gallery-lightbox__item.is-active [data-lightbox-zoom-wrap]', lightbox);
        if (!activeWrap) return;
        isZoomed = !isZoomed;
        activeWrap.classList.toggle('is-zoomed', isZoomed);
        zoomToggleBtn.setAttribute('aria-pressed', isZoomed ? 'true' : 'false');
        if (isZoomed) {
          dispatchAnalyticsEvent('zoom_activated', { index: lightboxIndex });
        }
      });
    }

    lightbox.addEventListener('mousemove', function (e) {
      if (!isZoomed) return;
      var activeWrap = qs('.proflex-gallery-lightbox__item.is-active [data-lightbox-zoom-wrap]', lightbox);
      if (!activeWrap) return;
      var rect = activeWrap.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      activeWrap.style.transformOrigin = x + '% ' + y + '%';
    });

    var lbTouchStartX = 0;
    var lbTouchEndX = 0;
    var stage = qs('[data-lightbox-stage]', lightbox);
    if (stage) {
      stage.addEventListener('touchstart', function (e) {
        lbTouchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      stage.addEventListener('touchend', function (e) {
        lbTouchEndX = e.changedTouches[0].screenX;
        var delta = lbTouchEndX - lbTouchStartX;
        if (!isZoomed && Math.abs(delta) > 40) {
          if (delta < 0) {
            showLightboxItem(lightboxIndex + 1);
          } else {
            showLightboxItem(lightboxIndex - 1);
          }
        }
      }, { passive: true });
    }

    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showLightboxItem(lightboxIndex - 1);
      if (e.key === 'ArrowRight') showLightboxItem(lightboxIndex + 1);
    });

    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
  }

  /* -----------------------------
     Exercise library filter
  ----------------------------- */
  function initExerciseFilter() {
    var filterBar = qs('[data-exercise-filters]');
    var grid = qs('[data-exercise-grid]');
    if (!filterBar || !grid) return;

    var buttons = qsa('[data-exercise-filter]', filterBar);
    var cards = qsa('[data-exercise-category]', grid);

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var filter = btn.getAttribute('data-exercise-filter');

        buttons.forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
          b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
        });

        cards.forEach(function (card) {
          var category = card.getAttribute('data-exercise-category');
          var show = filter === 'all' || category === filter;
          card.style.display = show ? '' : 'none';
        });
      });
    });
  }

  /* -----------------------------
     Marquee: seamless auto-scroll
  ----------------------------- */
  function initMarquee() {
    var marquee = qs('[data-proflex-marquee]');
    if (!marquee || prefersReducedMotion) return;

    var track = qs('[data-marquee-track]', marquee);
    if (!track) return;

    track.classList.add('is-animating');
  }

  /* -----------------------------
     Scroll-fade reveal animations
  ----------------------------- */
  function initScrollFade() {
    var elements = qsa('[data-scroll-fade]');
    if (!elements.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    elements.forEach(function (el) { observer.observe(el); });
  }

  /* -----------------------------
     Parallax (hero floating product)
  ----------------------------- */
  function initParallax() {
    var elements = qsa('[data-scroll-parallax]');
    if (!elements.length || prefersReducedMotion) return;

    var ticking = false;

    function update() {
      elements.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        var offset = rect.top * 0.08;
        el.style.transform = 'translateY(' + offset + 'px)';
      });
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  /* -----------------------------
     Init on DOM ready
     Guarded so this file can only ever run its init sequence
     once per page load, even if the script tag is accidentally
     included twice. Every individual init is now wrapped in
     safeInit() so one feature's error can never disable
     any other feature (header, Buy Now, gallery, etc. are
     now fully independent of each other's success/failure).
  ----------------------------- */
  if (window.__proflexThemeInitialized) return;
  window.__proflexThemeInitialized = true;

  document.addEventListener('DOMContentLoaded', function () {
    safeInit('header', initHeader);
    safeInit('mobile drawer', initMobileDrawer);
    safeInit('lead modal', initLeadModal);
    safeInit('lead form', initLeadForm);
    safeInit('video tracking', initVideoTracking);
    safeInit('video modal', initVideoModal);
    safeInit('product gallery', initProflexProductGallery);
    safeInit('exercise filter', initExerciseFilter);
    safeInit('marquee', initMarquee);
    safeInit('scroll fade', initScrollFade);
    safeInit('parallax', initParallax);
  });
})();